import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useFeatures, useNotify, usePolicy } from '../../../../hooks';
import { useActiveProjectId } from '../../../../hooks/useActiveProjectId';
import { useApiClient } from '../../../../hooks/useRestAPIHooks';
import { enforce } from '../../../../util/abac';

/** Which part of the action-plan generation a thumb is about. */
export type ActionPlanFeedbackTarget = 'coach-narrative' | 'action-item';

/** Thumbs up/down. */
export type ActionPlanFeedbackSentiment = 'positive' | 'negative';

export type SubmitActionPlanFeedbackArgs = {
  /** Selected dedicated `zori-coach` CarePlan resource ID. */
  carePlanId: string;
  /** Exact action activity ID (`action-<uuid4>`); required for `action-item`. */
  activityId?: string;
  /**
   * The coach run that produced this action plan
   * (`"zori-pulse-coach/<task_id>"`), read from the selected CarePlan's exact
   * source-run tag. Feedback is tied to it so the nightly guidance summarizer
   * can attribute it back to the action-plan scan category.
   */
  sourceRunId: string;
  target: ActionPlanFeedbackTarget;
  sentiment: ActionPlanFeedbackSentiment;
  comment?: string;
};

export type UseActionPlanFeedback = {
  /** Gate: the Zori Pulse scan feature is on for this account. */
  zoriPulseEnabled: boolean;
  isSubmitting: boolean;
  /**
   * POSTs the feedback to the agents action-plan feedback endpoint. Resolves to
   * `true` when it was persisted to FHIR, regardless of whether the best-effort
   * Langfuse score succeeded. Never throws — failures surface via a toast.
   */
  submitFeedback: (args: SubmitActionPlanFeedbackArgs) => Promise<boolean>;
};

const messages = defineMessages({
  submitted: {
    id: 'actionPlan.feedback.submitted',
    defaultMessage:
      'Thanks — your feedback will help tune the next action plan.',
  },
  failed: {
    id: 'actionPlan.feedback.failed',
    defaultMessage: 'We could not record your feedback. Please try again.',
  },
});

const ZORI_PULSE_SCAN_AGENT = 'api-zori-pulse-scan';

/**
 * Routes clinician/health-coach feedback on the Zori Pulse action plan (the
 * coach narrative and individual action items) to the agents action-plan
 * feedback endpoint, tied to the coach run that produced the plan.
 *
 * Receives `subject_id` from the selected coaching workspace and reads
 * `project_id` from the active project. Gated on the `zoriPulseScan` feature
 * and the feedback endpoint's agent policy via {@link zoriPulseEnabled};
 * callers should hide the affordance when it is off.
 */
export const useActionPlanFeedback = (
  subjectId: string,
): UseActionPlanFeedback => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const apiClient = useApiClient();
  const hasFeature = useFeatures();
  const projectId = useActiveProjectId();
  const policy = usePolicy();

  const canInvokeZoriPulseScan =
    enforce('invokeAgent', policy as any, {
      agent: ZORI_PULSE_SCAN_AGENT,
    }) || enforce('accessAdmin', policy as any);
  const zoriPulseEnabled =
    hasFeature('zoriPulseScan') && canInvokeZoriPulseScan;

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const submitFeedback = React.useCallback(
    async (args: SubmitActionPlanFeedbackArgs): Promise<boolean> => {
      if (
        !zoriPulseEnabled ||
        !subjectId ||
        !projectId ||
        !args.carePlanId ||
        !args.sourceRunId ||
        (args.target === 'action-item' && !args.activityId)
      ) {
        notify(formatMessage(messages.failed), 'error');
        return false;
      }

      setIsSubmitting(true);
      const trimmedComment = args.comment?.trim();
      let didPersist = false;
      try {
        const response = await apiClient.request(
          'POST /v1/agents-v2/zori-pulse/action-plan/feedback',
          {
            subject_id: subjectId,
            project_id: projectId,
            care_plan_id: args.carePlanId,
            ...(args.activityId ? { activity_id: args.activityId } : {}),
            source_run_id: args.sourceRunId,
            target: args.target,
            sentiment: args.sentiment,
            ...(trimmedComment ? { comment: trimmedComment } : {}),
          },
        );
        didPersist = response.data.persisted;
        notify(
          formatMessage(didPersist ? messages.submitted : messages.failed),
          didPersist ? 'success' : 'error',
        );
      } catch (err) {
        console.error(err);
        notify(formatMessage(messages.failed), 'error');
      } finally {
        setIsSubmitting(false);
      }
      return didPersist;
    },
    [apiClient, formatMessage, notify, projectId, subjectId, zoriPulseEnabled],
  );

  return { zoriPulseEnabled, isSubmitting, submitFeedback };
};

export default useActionPlanFeedback;
